'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CarouselSlide {
  id: string;
  backgroundImage: string;
  productImage?: string;
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  alt: string;
  features?: Array<{
    icon?: string;
    text: string;
  }>;
  hoverTitle?: string;
  hoverDescription?: string;
  hoverCtaText?: string;
  hoverCtaLink?: string;
  hoverDotPosition?: {
    left: string;
    top: string;
  };
}

interface ReusableCarouselSectionProps {
  title?: string;
  slides: CarouselSlide[];
  interval?: number;
  height?: number;
  heightClass?: string;
  className?: string;
  enableHover?: boolean;
  hoverPanelWidth?: string;
  contentMaxWidth?: string;
}

export function ReusableCarouselSection({
  title,
  slides,
  interval = 5000,
  height = 480,
  heightClass,
  className = '',
  enableHover = false,
  hoverPanelWidth = 'max-w-5xl',
  contentMaxWidth = 'max-w-4xl'
}: ReusableCarouselSectionProps) {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const direction = useRef<number>(1);

  // Only show multiple slides if there are more than 1
  const hasMultipleSlides = slides && slides.length > 1;

  // Check if current slide has hover content
  const currentSlide = slides[currentIndex];
  const hasHoverContent =
    currentSlide &&
    (currentSlide.hoverTitle ||
      currentSlide.hoverDescription ||
      currentSlide.hoverCtaText ||
      (currentSlide.features && currentSlide.features.length > 0) ||
      currentSlide.productImage);

  // Only enable hover if both enableHover prop is true AND there's hover content
  const shouldEnableHover = enableHover && hasHoverContent;

  const goToPrevious = useCallback(() => {
    if (!hasMultipleSlides) return;
    direction.current = -1;
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSlideProgress(0);
    // Reset hover state when navigating
    setIsHovered(false);
  }, [currentIndex, slides?.length, hasMultipleSlides]);

  const goToNext = useCallback(() => {
    if (!hasMultipleSlides) return;
    direction.current = 1;
    const isLastSlide = currentIndex === slides.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSlideProgress(0);
    // Reset hover state when navigating
    setIsHovered(false);
  }, [currentIndex, slides?.length, hasMultipleSlides]);

  const goToSlide = useCallback(
    (index: number) => {
      if (!hasMultipleSlides) return;
      direction.current = index > currentIndex ? 1 : -1;
      setCurrentIndex(index);
      setSlideProgress(0);
      // Reset hover state when navigating
      setIsHovered(false);
    },
    [currentIndex, hasMultipleSlides]
  );

  // Animation function to update progress
  const updateProgress = useCallback(
    (timestamp: number) => {
      if (!lastUpdateTimeRef.current) lastUpdateTimeRef.current = timestamp;

      if (!isPaused && isVisible && hasMultipleSlides && !isHovered) {
        const elapsed = timestamp - lastUpdateTimeRef.current;
        const progress = Math.min((elapsed / interval) * 100, 100);
        setSlideProgress(progress);

        if (progress >= 100) {
          goToNext();
          lastUpdateTimeRef.current = timestamp;
        }
      }

      animationRef.current = requestAnimationFrame(updateProgress);
    },
    [isPaused, isVisible, interval, goToNext, hasMultipleSlides, isHovered]
  );

  // Set up auto transition and intersection observer
  useEffect(() => {
    if (!hasMultipleSlides) return;

    // Set up Intersection Observer to detect visibility
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setIsVisible(entry.isIntersecting);
          if (entry.isIntersecting) {
            lastUpdateTimeRef.current = performance.now();
          }
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    lastUpdateTimeRef.current = performance.now();
    animationRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [updateProgress, hasMultipleSlides]);

  // Handlers to pause/resume on user interaction
  const pauseAutoTransition = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeAutoTransition = useCallback(() => {
    setIsPaused(false);
    lastUpdateTimeRef.current = performance.now();
  }, []);

  const handleMouseEnter = useCallback(() => {
    pauseAutoTransition();
  }, [pauseAutoTransition]);

  const handleMouseLeave = useCallback(() => {
    // Only resume auto transition if hover wasn't manually triggered
    if (!isHovered) {
      resumeAutoTransition();
    }
  }, [resumeAutoTransition, isHovered]);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isHovered) {
        setIsHovered(false);
        resumeAutoTransition();
      }
    },
    [isHovered, resumeAutoTransition]
  );

  const handleTriggerInteraction = useCallback(() => {
    if (shouldEnableHover) {
      setIsHovered(true);
      pauseAutoTransition();
    }
  }, [shouldEnableHover, pauseAutoTransition]);

  const handleTriggerLeave = useCallback(() => {
    if (shouldEnableHover) {
      // Don't automatically reset hover state - let it persist until navigation
      // setIsHovered(false);
      // resumeAutoTransition();
    }
  }, [shouldEnableHover]);

  // Early return if no slides
  if (!slides || slides.length === 0) {
    return null;
  }

  // Fallback to first slide if currentIndex is out of bounds
  if (!currentSlide) {
    return null;
  }

  return (
    <section className={`w-full ${className}`}>
      {/* Optional Title */}
      {title && (
        <div className="mb-3 text-center lg:mb-6">
          <h2 className="text-2xl font-bold text-gray-900 lg:text-5xl">{title}</h2>
        </div>
      )}

      <div
        ref={containerRef}
        className={`group relative w-full overflow-hidden ${heightClass || ''}`}
        style={heightClass ? {} : { height: `${height}px` }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleContainerClick}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={currentSlide.backgroundImage}
            alt={currentSlide.alt}
            fill
            className="object-cover"
            priority
            sizes="100vw"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
        </div>

        {/* Custom Hover Trigger Dot */}
        {shouldEnableHover && !isHovered && (
          <button
            className="focus:ring-opacity-50 absolute z-20 transition-all duration-300 hover:scale-110 focus:ring-2 focus:ring-white focus:outline-none"
            style={{
              width: '50px',
              height: '50px',
              left: currentSlide.hoverDotPosition?.left || '35.94%',
              top: currentSlide.hoverDotPosition?.top || '35.42%'
            }}
            onClick={handleTriggerInteraction}
            onMouseEnter={handleTriggerInteraction}
            aria-label="View product details"
          >
            {/* Outer border circle - Ellipse 16 */}
            <div
              className="absolute inset-0 rounded-full border-2"
              style={{
                borderColor: '#14BBD3'
              }}
            />

            {/* Inner filled circle - Ellipse 15 */}
            <div
              className="absolute rounded-full"
              style={{
                left: '20%',
                right: '20%',
                top: '20%',
                bottom: '20%',
                backgroundColor: '#14BBD3'
              }}
            />
          </button>
        )}

        {/* Slide Indicators - Top Right */}
        {hasMultipleSlides && (
          <div className="absolute top-10 right-15 z-30">
            <div className="flex items-center gap-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    goToSlide(index);
                    pauseAutoTransition();
                    setTimeout(resumeAutoTransition, 3000);
                  }}
                  className={`relative h-1 overflow-hidden rounded-full transition-all duration-300 ${
                    index === currentIndex ? 'w-20 bg-gray-300' : 'w-6 bg-gray-500'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  {index === currentIndex && (
                    <div
                      className="absolute top-0 left-0 h-full bg-cyan-400 transition-all duration-100 ease-linear"
                      style={{
                        width: `${slideProgress}%`
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Default Content Container */}
        <div
          className={`relative z-10 flex h-full flex-col justify-end px-4 py-3 transition-opacity duration-300 sm:px-12 sm:py-10 xl:px-21 ${
            shouldEnableHover && isHovered ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {/* Main Content */}
          <div className="mb-0 flex mr-25 sm:mr-0 flex-col items-start justify-between gap-6 sm:flex-row sm:items-center sm:gap-16 xl:gap-28">
            <div className={`${contentMaxWidth}`}>
              <h3 className="mb-4 text-2xl leading-tight font-bold text-white xl:text-4xl">
                {currentSlide.title}
              </h3>
              <p className="mb-6 hidden text-lg leading-relaxed font-light text-white sm:block xl:text-xl">
                {currentSlide.description}
              </p>
            </div>

            {/* CTA Button */}
            {currentSlide.ctaText && (
              <Link
                href={currentSlide.ctaLink}
                className="flex items-center justify-center gap-2 rounded border border-gray-300 bg-white px-5 py-2 text-base font-medium whitespace-nowrap text-gray-900 transition-colors hover:bg-gray-50"
              >
                {currentSlide.ctaText}
              </Link>
            )}
          </div>
        </div>

        {/* Hover State - Slide up from bottom */}
        {shouldEnableHover && (
          <div
            className={`pointer-events-none absolute inset-y-0 left-0 z-40 w-4/5 sm:w-2/3 transition-transform duration-500 ease-out ${
              isHovered ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Main hover container - Full width and height */}
            <div
              className={`relative flex h-full w-full flex-col items-end justify-center rounded-r-md bg-white px-8 py-2 xl:px-12 xl:py-4 ${isHovered ? 'pointer-events-auto' : 'pointer-events-none'}`}
            >
              {/* Content wrapper - Responsive with max width */}
              <div className="flex h-full max-h-[450px] w-full max-w-6xl flex-col items-end">
                {/* Main content row */}
                <div className="flex h-full w-full flex-row items-center">
                  {/* Product details section */}
                  <div className="flex h-full max-w-md min-w-0 flex-1 flex-col items-start justify-center gap-6 xl:max-w-lg xl:gap-8">
                    {/* Header section */}
                    <div className="flex w-full flex-col items-start gap-4 xl:gap-6">
                      {/* Title and description */}
                      <div className="flex w-full flex-col items-start gap-1 xl:gap-2">
                        <h4
                          className="w-full text-xl leading-tight font-medium text-gray-800 xl:text-2xl"
                          style={{ userSelect: 'text', cursor: 'text' }}
                        >
                          {currentSlide.hoverTitle || currentSlide.title}
                        </h4>
                        <p
                          className="w-full text-sm leading-relaxed font-normal text-gray-600 xl:text-base"
                          style={{ userSelect: 'text', cursor: 'text' }}
                        >
                          {currentSlide.hoverDescription || currentSlide.description}
                        </p>
                      </div>

                      {/* Features grid */}
                      {currentSlide.features && currentSlide.features.length > 0 && (
                        <div className="flex w-full max-w-sm flex-col items-start gap-3 xl:max-w-md xl:gap-4">
                          {/* First row */}
                          <div className="flex w-full flex-row items-center gap-4 xl:gap-7">
                            {currentSlide.features.slice(0, 2).map((feature, index) => (
                              <div
                                key={index}
                                className="flex min-w-0 flex-1 flex-row items-center gap-2"
                              >
                                {feature.icon ? (
                                  <div className="h-5 w-5 flex-shrink-0 xl:h-6 xl:w-6">
                                    <Image
                                      src={feature.icon}
                                      alt=""
                                      width={20}
                                      height={20}
                                      className="h-full w-full object-contain"
                                    />
                                  </div>
                                ) : (
                                  <div className="h-5 w-5 flex-shrink-0 rounded-full bg-gray-300 xl:h-6 xl:w-6" />
                                )}
                                <span
                                  className="truncate text-xs leading-tight font-normal text-black xl:text-sm"
                                  style={{ userSelect: 'text', cursor: 'text' }}
                                >
                                  {feature.text}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Second row */}
                          {currentSlide.features.length > 2 && (
                            <div className="flex w-full flex-row items-center gap-4 xl:gap-7">
                              {currentSlide.features.slice(2, 4).map((feature, index) => (
                                <div
                                  key={index + 2}
                                  className="flex min-w-0 flex-1 flex-row items-center gap-2"
                                >
                                  {feature.icon ? (
                                    <div className="h-5 w-5 flex-shrink-0 xl:h-6 xl:w-6">
                                      <Image
                                        src={feature.icon}
                                        alt=""
                                        width={20}
                                        height={20}
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  ) : (
                                    <div className="h-5 w-5 flex-shrink-0 rounded-full bg-gray-300 xl:h-6 xl:w-6" />
                                  )}
                                  <span
                                    className="truncate text-xs leading-tight font-normal text-black xl:text-sm"
                                    style={{ userSelect: 'text', cursor: 'text' }}
                                  >
                                    {feature.text}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const linkToUse = currentSlide.hoverCtaLink || currentSlide.ctaLink;
                        if (linkToUse) {
                          window.open(linkToUse, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      className="relative inline-flex cursor-pointer flex-row items-center justify-center rounded border px-3 py-2 font-normal text-white transition-colors hover:bg-cyan-500 focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:outline-none xl:px-4 xl:py-2"
                      style={{
                        backgroundColor: '#14BBD3',
                        borderColor: '#CCD2D9',
                        fontSize: '12px',
                        lineHeight: '14px'
                      }}
                    >
                      <span className="pointer-events-none xl:text-sm">
                        {currentSlide.hoverCtaText || 'Buy Now'}
                      </span>
                    </button>
                  </div>

                  {/* Product Image */}
                  {currentSlide.productImage && (
                    <div className="relative h-64 w-64 flex-shrink-0 xl:h-80 xl:w-80 2xl:h-96 2xl:w-96 hidden sm:block">
                      <Image
                        src={currentSlide.productImage}
                        alt={currentSlide.hoverTitle || currentSlide.title}
                        fill
                        className="object-contain"
                        sizes="(max-width: 1280px) 256px, (max-width: 1536px) 320px, 384px"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Left Arrow - Bottom right corner */}
              <button
                className="absolute right-4 bottom-4 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white text-black shadow-sm transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-gray-400 focus:outline-none xl:right-6 xl:bottom-6 xl:h-8 xl:w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsHovered(false);
                  resumeAutoTransition();
                }}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  setIsHovered(false);
                  resumeAutoTransition();
                }}
                aria-label="Go back"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="pointer-events-none xl:h-5 xl:w-5"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Navigation Arrows - Only show if multiple slides */}
        {hasMultipleSlides && !isHovered && (
          <>
            <button
              onClick={() => {
                goToPrevious();
                pauseAutoTransition();
                setTimeout(resumeAutoTransition, 3000);
              }}
              className="absolute top-1/2 left-4 z-20 -translate-y-1/2 transform rounded-full bg-black/30 p-2 text-white transition-colors hover:bg-black/50"
              aria-label="Previous slide"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <button
              onClick={() => {
                goToNext();
                pauseAutoTransition();
                setTimeout(resumeAutoTransition, 3000);
              }}
              className="absolute top-1/2 right-4 z-20 -translate-y-1/2 transform rounded-full bg-black/30 p-2 text-white transition-colors hover:bg-black/50"
              aria-label="Next slide"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </>
        )}
      </div>
    </section>
  );
}
